import { Theme } from '@core/enums';
import { Device, until } from '@helpers/media';
import { makeHiddenNonFocusable, ResponsiveConfig } from '@helpers/slick';
import { useInView } from '@hooks/useInView';
import useMedia from '@hooks/useMedia';
import CareerCard from '@stories/Elements/Cards/CareerCard/CareerCard';
import CategoryCard, { CategoryCardModel } from '@stories/Elements/Cards/CategoryCard/CategoryCard';
import ProductCard from '@stories/Elements/Cards/ProductCard/ProductCard';
import SystemCard, { SystemCardModel } from '@stories/Elements/Cards/SystemCard/SystemCard';
import TradeCard, { TradeCardModel } from '@stories/Elements/Cards/TradeCard/TradeCard';
import SliderButton from '@stories/Elements/Media/SliderButton/SliderButton';
import React, { ReactNode, useRef, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import { CardSliderStyles } from './CardSlider.styles';

export enum CardType {
  Category = 0,
  System = 1,
  Trade = 2,
  Product = 3,
  Career = 4,
}

type CardModel = CategoryCardModel | SystemCardModel | TradeCardModel | ProductViewModel | CareerCardModel;

export interface CardSliderProps {
  /**
   * Decides which set of cards to show based of CardType
   */
  cardType: CardType;
  cardTheme: Theme;
  /**
   * Used to set visible slides within the container, need to pass a responsive value if mobile/desktop differ.
   */
  slidesToShow: number;
  /**
   * Responsive settings for the slider
   */
  responsiveConfig: ResponsiveConfig;
  items: CardModel[];
  buttonText?: string;
  includeProgressBar?: boolean;
  onClick?: (item: ProductViewModel, index: number) => void;
}

const CardSlider: React.FC<CardSliderProps> = ({
  cardType,
  slidesToShow,
  responsiveConfig,
  items,
  buttonText,
  cardTheme,
  includeProgressBar = false,
  onClick,
}) => {
  const responsiveThreshold = useMedia([until(Device.Tablet)], [0.1], 0.5);
  const [elementRef, inView] = useInView<HTMLDivElement>({ threshold: responsiveThreshold });
  const sliderRef = useRef<Slider>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const currentSlidePercentage = (activeIndex + 1) / (items.length + 1 - slidesToShow);

  const slickSettings: Settings = {
    arrows: false,
    dots: false,
    infinite: false,
    speed: 500,
    ...responsiveConfig,
    slidesToScroll: 1,
    swipeToSlide: true,
    touchThreshold: 50,
    edgeFriction: 0,
    beforeChange: (oldIndex, newIndex) => updateActiveIndex(oldIndex, newIndex),
    afterChange: makeHiddenNonFocusable(elementRef),
    onInit: makeHiddenNonFocusable(elementRef),
  };

  const next = (): void => {
    if (sliderRef.current === null || activeIndex === items.length - slidesToShow) {
      return;
    }

    setActiveIndex(activeIndex + 1);
    sliderRef.current.slickNext();
  };

  const previous = (): void => {
    if (sliderRef.current === null || activeIndex === 0) {
      return;
    }

    setActiveIndex(activeIndex - 1);
    sliderRef.current.slickPrev();
  };

  const updateActiveIndex = (oldIndex: number, newIndex: number): void => {
    setActiveIndex(newIndex);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function renderCard(cardType: CardType, item: any, index: number): ReactNode {
    switch (cardType) {
      default:
      case CardType.Category: {
        return (
          <CategoryCard
            key={item.categoryId}
            category={item}
            index={index}
            rowItems={useMedia(
              [until(Device.Tablet), until(Device.TabletLarge), until(Device.Desktop), until(Device.ActualDesktop)],
              [1, 2, 3, 5],
              6,
            )}
            isCentered={true}
            imageSizes="400px"
            theme={cardTheme}
            textOnly={true}
          />
        );
      }

      case CardType.System: {
        return (
          <SystemCard
            key={item.id}
            system={item}
            index={index}
            rowItems={useMedia([until(Device.Tablet), until(Device.TabletLarge)], [1, 2], 3)}
            imageSizes="50vw"
            theme={cardTheme}
          />
        );
      }

      case CardType.Trade: {
        return (
          <TradeCard
            key={item.id}
            trade={item}
            index={index}
            rowItems={useMedia(
              [until(Device.Tablet), until(Device.TabletLarge), until(Device.Desktop), until(Device.ActualDesktop)],
              [1, 2, 3, 5],
              6,
            )}
            imageSizes={'350px'}
          />
        );
      }

      case CardType.Product: {
        return (
          <ProductCard
            key={item.agilityId}
            product={item}
            index={index}
            buttonText={buttonText !== undefined ? buttonText : ''}
            rowItems={useMedia(
              [until(Device.Tablet), until(Device.TabletLarge), until(Device.Desktop), until(Device.ActualDesktop)],
              [1, 2, 3, 5],
              6,
            )}
            onClick={onClick ? () => onClick(item, index) : undefined}
          />
        );
      }

      case CardType.Career: {
        return <CareerCard key={item.agilityId} career={item} index={index} />;
      }
    }
  }

  const renderControls = (): ReactNode => {
    return (
      <CardSliderStyles.SliderControls inView={inView}>
        <SliderButton
          theme={cardTheme === Theme.Black && cardType !== CardType.Trade ? 'black' : 'red'}
          direction="left"
          onClick={previous}
          disabled={activeIndex === 0}
        />
        <SliderButton
          theme={cardTheme === Theme.Black && cardType !== CardType.Trade ? 'black' : 'red'}
          direction="right"
          onClick={next}
          disabled={activeIndex === items.length - slidesToShow}
        />
      </CardSliderStyles.SliderControls>
    );
  };

  return (
    <CardSliderStyles.Container ref={elementRef}>
      <CardSliderStyles.SliderWrapper onDragStart={(e) => e.preventDefault()}>
        <Slider ref={sliderRef} {...slickSettings}>
          {items.map((item, index: number) => renderCard(cardType, item, index))}
        </Slider>
        {!includeProgressBar && renderControls()}
        {includeProgressBar && (
          <CardSliderStyles.Bottom>
            <CardSliderStyles.SliderProgressBar themeOption={cardTheme}>
              <CardSliderStyles.SliderProgressBackground />
              <CardSliderStyles.SliderProgressFill percentage={currentSlidePercentage} />
            </CardSliderStyles.SliderProgressBar>
            {renderControls()}
          </CardSliderStyles.Bottom>
        )}
      </CardSliderStyles.SliderWrapper>
    </CardSliderStyles.Container>
  );
};

export default CardSlider;
